/**
 * Internal User Contracts
 *
 * DATABASE-FIRST: Schemas match PostgreSQL exactly
 * Source: MCP postgres queries on 2026-01-02
 *
 * Function: create_or_update_user_v2
 * Table: users
 *
 * Used by:
 * - main-bot → backend (user registration on /start)
 * - backend internal API (user profile, status checks)
 */
import { z } from 'zod';
import { bigintString } from '../common.js';
// =============================================================================
// CREATE OR UPDATE USER
// Function: create_or_update_user_v2(
//   p_telegram_id bigint,
//   p_username varchar,
//   p_first_name varchar,
//   p_last_name varchar,
//   p_phone_number varchar DEFAULT NULL,
//   p_game_account varchar DEFAULT NULL
// )
// Returns: TABLE(action text, user_created boolean, fields_updated text[])
// =============================================================================
/**
 * User registration request
 * Endpoint: POST /api/internal/user/register
 *
 * NOTE: telegramId accepts both string and number because:
 * - Bot sends number (ctx.from.id)
 * - Frontend sends string
 * Both are coerced to string for consistent handling.
 */
export const registerUserRequestSchema = z.object({
    telegramId: z.union([z.string(), z.number()]).transform(String), // Accepts both, outputs string
    username: z.string().nullable().optional(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    phoneNumber: z.string().nullable().optional(),
    gameAccountCode: z.string().nullable().optional(),
});
/**
 * create_or_update_user_v2 stored procedure result
 */
export const createOrUpdateUserResultSchema = z.object({
    action: z.enum(['created', 'updated', 'unchanged']),
    userCreated: z.boolean(),
    fieldsUpdated: z.array(z.string()),
});
/**
 * User registration response
 * Endpoint: POST /api/internal/user/register
 */
export const registerUserResponseSchema = z.object({
    success: z.boolean(),
    user: z.object({
        telegramId: bigintString,
        username: z.string().nullable(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        gameAccountCode: z.string().nullable(),
        isBanned: z.boolean(),
        hasDeposited: z.boolean(),
        isNew: z.boolean(),
    }).nullable(),
    action: z.enum(['created', 'updated', 'unchanged']).optional(),
    fieldsUpdated: z.array(z.string()).optional(),
    error: z.string().optional(),
});
// =============================================================================
// GET USER PROFILE
// Endpoint: GET /api/internal/user/:telegramId/profile
// =============================================================================
/**
 * User profile response
 * Full user profile for internal use
 */
export const getUserProfileResponseSchema = z.object({
    success: z.boolean(),
    user: z.object({
        telegramId: bigintString,
        username: z.string().nullable(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        gameAccountCode: z.string().nullable(),
        isBanned: z.boolean(),
        hasDeposited: z.boolean(),
        totalDeposit: z.number(),
        totalBonus: z.number(),
        totalDepositPlusBonus: z.number(),
        totalWithdrawal: z.number(),
        netTotal: z.number(),
        minimumWithdrawal: z.number(),
        lastDepositRef: z.string().nullable(),
        refereeId: z.string().nullable(),
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
    }).nullable(),
    error: z.string().optional(),
});
// =============================================================================
// GET USER STATUS
// Endpoint: GET /api/internal/user/:telegramId/status
// Quick check for ban status and basic info
// =============================================================================
/**
 * User status response
 */
export const getUserStatusResponseSchema = z.discriminatedUnion('exists', [
    // User found
    z.object({
        success: z.literal(true),
        exists: z.literal(true),
        isBanned: z.boolean(),
        gameAccountCode: z.string().nullable(),
        hasDeposited: z.boolean(),
    }),
    // User not found
    z.object({
        success: z.literal(true),
        exists: z.literal(false),
    }),
]);
// =============================================================================
// MARK USER BLOCKED
// Endpoint: POST /api/internal/user/:telegramId/mark-blocked
// Called when bot receives 403 (user blocked the bot)
// =============================================================================
/**
 * Mark user blocked response
 */
export const markUserBlockedResponseSchema = z.object({
    success: z.boolean(),
});
// =============================================================================
// BULK USER OPERATIONS
// Endpoints for admin-bot bulk messaging
// =============================================================================
/**
 * Get active users request params
 * Endpoint: GET /api/internal/users/active
 */
export const getActiveUsersQuerySchema = z.object({
    limit: z.coerce.number().min(1).max(1000).default(100),
    offset: z.coerce.number().min(0).default(0),
});
/**
 * Active user summary (for bulk messaging)
 */
export const activeUserSummarySchema = z.object({
    telegramId: bigintString,
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    blockedBot: z.boolean(),
});
/**
 * Get active users response
 */
export const getActiveUsersResponseSchema = z.object({
    success: z.boolean(),
    users: z.array(activeUserSummarySchema),
    total: z.number(),
    error: z.string().optional(),
});
/**
 * Get active user count response
 * Endpoint: GET /api/internal/users/active/count
 */
export const getActiveUserCountResponseSchema = z.object({
    success: z.boolean(),
    count: z.number(),
    error: z.string().optional(),
});
// =============================================================================
// ADMIN AUDIT LOG
// Endpoint: POST /api/internal/admin/log-action
// =============================================================================
/**
 * Admin action log request
 */
export const logAdminActionRequestSchema = z.object({
    staffId: z.string(),
    action: z.string(),
    targetType: z.enum(['user', 'deposit', 'withdrawal', 'cascade', 'bonus', 'system']).optional(),
    targetId: z.string().optional(),
    details: z.record(z.unknown()).optional(),
});
/**
 * Admin action log response
 */
export const logAdminActionResponseSchema = z.object({
    success: z.boolean(),
    error: z.string().optional(),
});
//# sourceMappingURL=user.js.map